import type { LegalDoc } from "./types";

// Simplified Chinese (简体中文). 参考政策的译文 (v1.0, 2026年9月1日)。
const gdprZh: LegalDoc = {
  title: "隐私与数据保护政策 (GDPR / RGPD)",
  effectiveDate: "2026年11月1日",
  version: "1.1",
  intro:
    "本政策说明 Photohawk 如何依据欧盟《通用数据保护条例》(GDPR)及其法国对应法规 RGPD (Règlement Général sur la Protection des Données)，以及英国 GDPR，处理个人数据。RGPD 与 GDPR 指的是同一部法律。",
  sections: [
    {
      heading: "1. 我们是谁",
      blocks: [
        {
          type: "p",
          text: "Photohawk 是一个活动摄影平台，让摄影师和主办方能够发布、搜索并出售活动照片。",
        },
        {
          type: "ul",
          items: [
            "法律实体：CLOUDCONCUR LTD（公司编号 13399913），在英格兰及威尔士注册的公司，以 Photohawk 为商号经营。",
            "注册地址：23 Caldey Gardens, Ingleby Barwick, Stockton-on-Tees, TS17 5HW, United Kingdom。",
            "数据保护官 (DPO)：Andrew Hammond — 联系方式 support@photohawk.com。",
            "欧盟代表 (GDPR Art. 27)：我们尚未指定欧盟代表，理由是我们对欧盟居民数据的处理是偶发性的，并非我们的主要活动 (Art. 27(2))。我们会持续审视这一点，若我们的处理活动发生变化，将指定代表。",
            "监管机构：英国信息专员办公室 (ICO) 负责英国事务，欧盟各监管机构（包括法国 CNIL）负责欧盟境内个人的事务。",
          ],
        },
      ],
    },
    {
      heading: "2. 我们的两种角色 — 控制者与处理者",
      blocks: [
        {
          type: "p",
          text: "Photohawk 面对两类人群，而我们对每一类的法律角色各不相同：",
        },
        {
          type: "ul",
          items: [
            "摄影师和主办方（我们的客户）。对于您的账户数据（姓名、电子邮件、业务信息、登录凭证、账单及付款信息），Photohawk 是数据控制者。",
            "活动参与者和购买者（您的客户）。对于照片中人物及购买照片者的数据，您（摄影师／主办方）是数据控制者，而 Photohawk 是您的数据处理者 — 我们按照您的指示处理这些数据，以运行您所设置的图库、搜索和销售功能。此关系由《数据处理协议》（见 §11）约束。",
          ],
        },
      ],
    },
    {
      heading: "3. 我们处理哪些个人数据",
      blocks: [
        { type: "p", text: "对于摄影师／主办方（我们是控制者）：" },
        {
          type: "ul",
          items: [
            "身份与联系信息：姓名、电子邮件、电话、企业名称、地址。",
            "账户与安全：登录凭证（通过 AWS）、会话。",
            "财务信息：订阅／账单数据（通过 Stripe）以及付款明细（通过 Trolley）。",
          ],
        },
        { type: "p", text: "对于活动参与者／购买者（我们代表主办方作为处理者）：" },
        {
          type: "ul",
          items: [
            "身份与联系信息：姓名、电子邮件、电话号码、邮寄／账单地址。",
            "参与者的照片。",
            "生物特征数据 — 在启用人脸／自拍搜索的情况下（见 §4）。",
            "订单与支付数据（通过我们的支付服务商处理；我们不存储完整的卡号）。",
            "营销同意状态，即参与者选择同意的情况。",
          ],
        },
      ],
    },
    {
      heading: "4. 人脸与自拍搜索（特殊类别的生物特征数据）",
      blocks: [
        {
          type: "p",
          text: "当主办方启用自拍／人脸搜索时，Photohawk 会创建人脸的数学表示，以便参与者能够找到自己的照片。",
        },
        {
          type: "p",
          text: "人脸搜索为可选功能。我们仅在人脸数据所来自的图库仍可用于人脸搜索期间保留这些数据（数字化的人脸模板，而非照片），并最迟在照片上传 12 个月后将其永久删除；如果图库被关闭或人脸搜索被关闭，或者您要求我们删除或撤回同意，我们会在 30 天内将其删除。为搜索而上传的自拍仅用于该次搜索，不会被保存。",
        },
        {
          type: "link",
          text: "详细信息：",
          label: "生物特征数据保留政策",
          href: "/biometric-data-retention",
        },
        {
          type: "ul",
          items: [
            "目的：仅用于将某人与其本人的活动照片进行匹配。",
            "存储地点：在欧盟境内处理和存储（AWS，爱尔兰区域）。",
            "保留期限：人脸数据最迟在照片上传 12 个月后删除；在图库关闭或人脸搜索关闭后 30 天内删除；在提出删除请求或撤回同意后 30 天内删除 — 以先发生者为准（见生物特征数据保留政策）。",
            "法律依据 (GDPR Art. 9)：生物特征数据属于特殊类别，受到额外保护：它仅用于让人们在该图库中找到自己的照片。作为参与者的数据控制者，图库管理员决定人脸搜索是否适合其活动、在活动举办地是否被允许。如果当地法律要求取得被拍摄者的同意，管理员必须已取得该同意，例如通过活动报名；如果没有，则应改用号码布和时间搜索，这种搜索不使用人脸数据。Photohawk 提供产品内的同意步骤：参与者在其自拍被用于人脸搜索之前会被要求表示同意，并且可以拒绝。",
            "未成年人：见 §13。",
          ],
        },
      ],
    },
    {
      heading: "5. 我们为何使用数据及我们的法律依据",
      blocks: [
        {
          type: "ul",
          items: [
            "提供服务（托管图库、运行搜索、处理订单）：合同的履行。",
            "支付、付款、税务和会计记录：合同的履行以及法律义务的遵守。",
            "人脸／自拍搜索 (Art. 9)：在使用其自拍之前取得搜索者的明确同意；对于被拍摄者，按 §4 所述。",
            "营销电子邮件／消息：仅在收件人同意（选择加入）的情况下进行，该同意可随时撤回。",
            "分析和广告 Cookie：仅在您同意的情况下，通过我们的 Cookie 横幅给出（见 §12）。",
            "安全、欺诈防范和服务改进：我们的合法利益，与您的权利相权衡。",
          ],
        },
      ],
    },
    {
      heading: "6. 您的数据存储在何处以及国际传输",
      blocks: [
        {
          type: "ul",
          items: [
            "数据驻留：参与者和账户数据 — 包括照片和生物特征人脸数据 — 托管在欧盟境内（AWS，爱尔兰 / eu-west-1）。我们的身份服务 (AWS) 同样位于欧盟。",
            "国际传输：我们的部分子处理者（见 §7）在欧盟／英国以外运营（例如在美国）。当个人数据被传输到欧盟／英国以外时，在适用充分性决定的情况下我们依据该决定，否则依据欧盟委员会的标准合同条款 (SCC) 以及英国国际数据传输附录，并在必要时辅以额外的保障措施。",
          ],
        },
      ],
    },
    {
      heading: "7. 子处理者",
      blocks: [
        {
          type: "p",
          text: "我们使用少数经过审核的服务商来提供服务，每一家均受数据处理协议约束（并在其于欧盟／英国以外处理数据时受 SCC／英国附录约束）：",
        },
        {
          type: "table",
          head: ["服务商", "目的", "涉及的数据"],
          rows: [
            ["Amazon Web Services (AWS)", "托管、存储、身份、人脸搜索、事务性电子邮件 — 欧盟区域", "所有个人数据、照片、生物特征数据"],
            ["Cloudflare（含 R2）", "CDN、自定义域名、对象存储", "照片、请求数据"],
            ["Stripe", "银行卡支付与订阅", "购买者及客户的支付数据"],
            ["Xendit", "支付（东南亚）", "购买者的支付数据"],
            ["Paystack", "支付（非洲）", "购买者的支付数据"],
            ["Trolley", "向摄影师付款", "收款人身份及银行明细"],
            ["Postmark", "事务性电子邮件", "姓名、电子邮件地址"],
            ["Twilio / WhatsApp", "照片分发与通知", "姓名、电话号码"],
            ["Google (Google Analytics)", "网站使用情况分析（基于同意）", "使用／设备数据、标识符"],
            ["Meta Platforms (Meta Pixel)", "广告与转化衡量（基于同意）", "使用数据、标识符"],
          ],
        },
        { type: "p", text: "可应要求提供最新的名单。" },
      ],
    },
    {
      heading: "8. 我们保留数据的时长（保留期限）",
      blocks: [
        {
          type: "ul",
          items: [
            "人脸／生物特征数据：最迟在照片上传 12 个月后删除，或按 §4 及生物特征数据保留政策所述更早删除。用于搜索的自拍不会被保存。",
            "照片和视频：保留由账户所有者（主办方／摄影师）控制。内容会一直保留，直到账户所有者不再保留为止；账户所有者可随时删除照片、视频、图库和参与者记录，我们会在其删除时随之删除。Photohawk 不对此类内容强加固定的保存期限。",
            "订单和财务记录：保留 7 年，以满足税务和会计义务。",
            "参与者／购买者的联系记录：由主办方控制 — 只要主办方保留即保留，主办方删除或收到经核实的删除请求时予以删除。",
            "营销名单：保留至同意被撤回为止。",
            "运营／安全日志保留 30 天。",
          ],
        },
      ],
    },
    {
      heading: "9. 我们如何保护数据",
      blocks: [
        {
          type: "ul",
          items: [
            "传输中：全程通过 HTTPS/TLS 加密。",
            "静态存储时：照片和内容存储在 AWS 和 Cloudflare R2 上，二者默认对静态数据加密；敏感凭证（例如关联账户令牌）另外使用 AWS 加密。",
            "访问控制：通过 AWS 进行经过身份验证的访问；员工访问权限仅限于运营服务所需的范围。",
          ],
        },
      ],
    },
    {
      heading: "10. 您的权利",
      blocks: [
        {
          type: "p",
          text: "依据 GDPR/RGPD 和英国 GDPR，您有权：访问您的数据；更正数据；删除数据（“被遗忘权”）；限制或反对处理；数据可携带；以及随时撤回同意。您还有权向监管机构投诉（英国的 ICO，或您当地的机构，例如法国的 CNIL）。",
        },
        { type: "p", text: "如何行使这些权利：" },
        {
          type: "ul",
          items: [
            "摄影师／主办方（我们是控制者）：请联系我们的 DPO，邮箱 support@photohawk.com。我们会在一个月内回复。",
            "活动参与者／购买者（主办方是控制者）：请联系运营您所参加活动的摄影师或主办方。摄影师可以在 Photohawk 内删除参与者的个人数据 — 包括其照片以及任何人脸搜索数据 — 而 Photohawk 会代表主办方执行经核实的请求。",
            "人脸数据：可在图库的自拍搜索中选择“提交请求”，或发送邮件至 support@photohawk.com，要求删除（或撤回同意）。我们会在 30 天内处理。",
          ],
        },
        {
          type: "p",
          text: "关闭 Photohawk 账户会取消账单，但本身并不会删除已存储的数据；如果您需要删除数据，请要求我们删除。",
        },
      ],
    },
    {
      heading: "11. 数据处理协议 (DPA)",
      blocks: [
        {
          type: "p",
          text: "对于摄影师／主办方，我们对您参与者数据的处理受依据 GDPR Art. 28 的《数据处理协议》约束，我们可应要求通过 support@photohawk.com 提供。",
        },
      ],
    },
    {
      heading: "12. Cookie 及类似技术",
      blocks: [
        {
          type: "ul",
          items: [
            "严格必要的 Cookie（用于登录、安全和核心功能）始终启用；它们无需征得同意。",
            "分析和广告 Cookie — Google Analytics 和 Meta Pixel（见 §7）— 属于非必要项，仅在您通过我们的 Cookie 同意横幅选择加入后才会加载。您可以随时通过横幅／Cookie 设置更改或撤回您的选择。拒绝不会影响服务的完整可用性。",
            "更多细节载于我们单独的《Cookie 政策》。",
          ],
        },
      ],
    },
    {
      heading: "13. 儿童数据",
      blocks: [
        {
          type: "p",
          text: "活动中可能包括未成年人。人脸搜索是否适合某项活动由图库管理员决定（见 §4）；在可能有未成年人在场、且当地法律要求取得被拍摄者同意的情况下，管理员必须为其取得父母或监护人的同意，否则应改用号码布和时间搜索。Photohawk 不会在没有法律要求的同意的情况下故意为儿童创建生物特征数据，产品内的同意步骤进一步强化了这一点。",
        },
      ],
    },
    {
      heading: "14. 数据泄露",
      blocks: [
        {
          type: "p",
          text: "如果发生个人数据泄露，Photohawk 会在知悉后不无故拖延地通知受影响的数据控制者，并协助其履行自身对监管机构和个人的通知义务 (GDPR Arts. 33–34)。",
        },
      ],
    },
    {
      heading: "15. 本政策的变更",
      blocks: [
        {
          type: "p",
          text: "我们可能会更新本政策；上方的“生效日期”显示当前版本。重大变更将会告知账户持有人。",
        },
      ],
    },
    {
      heading: "16. 联系方式",
      blocks: [
        {
          type: "p",
          text: "数据保护官 — Andrew Hammond。CLOUDCONCUR LTD（公司编号 13399913），以 Photohawk 为商号经营。电子邮件：support@photohawk.com · 23 Caldey Gardens, Ingleby Barwick, Stockton-on-Tees, TS17 5HW, United Kingdom。",
        },
      ],
    },
  ],
};

export default gdprZh;
